import { z } from 'zod';

/**
 * Enumerations shared by the API, the database CHECK constraints (D-012) and the clients.
 * Arrays are the source of truth; types and Zod schemas are derived from them.
 */

// ---------------------------------------------------------------- roles (D-006)

export const ROLES = ['owner', 'admin', 'manager', 'member', 'guest'] as const;
export type Role = (typeof ROLES)[number];
export const roleSchema = z.enum(ROLES);

/** Higher rank = more authority. */
export const ROLE_RANK: Readonly<Record<Role, number>> = Object.freeze({
  owner: 50,
  admin: 40,
  manager: 30,
  member: 20,
  guest: 10,
});

export function roleRank(role: Role): number {
  return ROLE_RANK[role];
}

/** True when `role` is at least as senior as `minimum`. */
export function isRoleAtLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

// ---------------------------------------------------------------- workspace members (D-005, D-023)

export const MEMBER_STATUSES = ['active', 'deactivated'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];
export const memberStatusSchema = z.enum(MEMBER_STATUSES);

// ---------------------------------------------------------------- guests (D-006)

export const GUEST_SCOPES = ['single_channel', 'multi_channel'] as const;
export type GuestScope = (typeof GUEST_SCOPES)[number];
export const guestScopeSchema = z.enum(GUEST_SCOPES);

// ---------------------------------------------------------------- OTP policy (D-008)

export const OTP_POLICIES = ['always', 'new_device', 'off'] as const;
export type OtpPolicy = (typeof OTP_POLICIES)[number];
export const otpPolicySchema = z.enum(OTP_POLICIES);
export const DEFAULT_OTP_POLICY: OtpPolicy = 'new_device';

// ---------------------------------------------------------------- invites (D-022)

export const INVITE_STATUSES = ['pending', 'accepted', 'revoked', 'expired'] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];
export const inviteStatusSchema = z.enum(INVITE_STATUSES);

// ---------------------------------------------------------------- users (D-005, D-049)

/** `users.kind`: bots and the per-workspace "Hub" system user are users too, without an email. */
export const USER_KINDS = ['human', 'bot', 'system'] as const;
export type UserKind = (typeof USER_KINDS)[number];
export const userKindSchema = z.enum(USER_KINDS);

// ---------------------------------------------------------------- workspaces (D-004)

/** `workspaces.status`. A suspended workspace refuses sign-in (operator action only). */
export const WORKSPACE_STATUSES = ['active', 'suspended'] as const;
export type WorkspaceStatus = (typeof WORKSPACE_STATUSES)[number];
export const workspaceStatusSchema = z.enum(WORKSPACE_STATUSES);

/** `workspace_members.presence_override` ("set yourself as away"). Live presence is in Redis. */
export const PRESENCE_OVERRIDES = ['auto', 'away'] as const;
export type PresenceOverride = (typeof PRESENCE_OVERRIDES)[number];

/** Theme names (`branding.defaultTheme`, member UI preferences). */
export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];
export const themeSchema = z.enum(THEMES);

// ---------------------------------------------------------------- sessions and clients (D-008, D-073)

/** API `clientType` on login, OTP verify and invite accept. `mobile` and `desktop` are "native". */
export const CLIENT_TYPES = ['web', 'mobile', 'desktop'] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];
export const clientTypeSchema = z.enum(CLIENT_TYPES);

/** True for clients that receive refresh and trusted-device tokens in the body (D-073). */
export function isNativeClient(clientType: ClientType): boolean {
  return clientType !== 'web';
}

/** `auth_sessions.client_kind`: the stored, more specific kind of device. */
export const SESSION_CLIENT_KINDS = ['web', 'desktop', 'ios', 'android', 'api'] as const;
export type SessionClientKind = (typeof SESSION_CLIENT_KINDS)[number];

/** `auth_sessions.revoked_reason`. */
export const SESSION_REVOKED_REASONS = [
  'logout',
  'user_revoked',
  'admin_revoked',
  'password_reset',
  'deactivated',
  'refresh_reuse',
  'sign_out_everywhere',
] as const;
export type SessionRevokedReason = (typeof SESSION_REVOKED_REASONS)[number];

/**
 * `auth_otp_challenges.purpose` (PLAN §3.1). Password reset codes live in their own global table
 * `auth_password_resets`, so they are not a challenge purpose.
 */
export const OTP_PURPOSES = ['login', 'email_change'] as const;
export type OtpPurpose = (typeof OTP_PURPOSES)[number];

/**
 * Who may vouch for an identity (`auth_identities.provider`).
 *
 * ONE entry, and that is the point (D-1109). Orbit holds no credentials: `account.comsky.ai` is
 * the only authority on who somebody is, and the per-workspace Google and Microsoft SSO that used
 * to sit here is gone — the provider federates to those itself, where the second factors and the
 * session lifetime live. A second entry here would be a second front door on the product.
 */
export const AUTH_IDENTITY_PROVIDERS = ['comsky'] as const;
export type AuthIdentityProvider = (typeof AUTH_IDENTITY_PROVIDERS)[number];

// ---------------------------------------------------------------- invites and email (D-009, D-022)

/** `invites.email_delivery_status` / `InviteDto.emailDelivery.status`. */
export const EMAIL_DELIVERY_STATUSES = ['sent', 'failed'] as const;
export type EmailDeliveryStatus = (typeof EMAIL_DELIVERY_STATUSES)[number];
export const emailDeliveryStatusSchema = z.enum(EMAIL_DELIVERY_STATUSES);

/**
 * Kind of an outgoing email (mailer templates, `GET /dev/mailbox?kind=`). Phase 2 adds the
 * unread-mentions digest (SPEC §4.6).
 */
export const MAIL_KINDS = [
  'invite',
  'otp',
  'password_reset',
  'password_changed',
  'mention_digest',
  'other',
  // Wave 5 (D-1020, §7.9): calendar invitations and the guest notices (code only, no DB CHECK).
  'meeting_invite',
  'meeting_update',
  'meeting_cancel',
  'webinar_notice',
  'webinar_reminder',
  'booking_notice',
] as const;
export type MailKind = (typeof MAIL_KINDS)[number];
export const mailKindSchema = z.enum(MAIL_KINDS);

// ---------------------------------------------------------------- profile fields (SPEC 4.5)

export const PROFILE_FIELD_TYPES = [
  'text',
  'long_text',
  'url',
  'email',
  'phone',
  'date',
  'select',
  'multi_select',
] as const;
export type ProfileFieldType = (typeof PROFILE_FIELD_TYPES)[number];
export const profileFieldTypeSchema = z.enum(PROFILE_FIELD_TYPES);

/** Field types whose definition carries `options`. */
export const PROFILE_FIELD_OPTION_TYPES = ['select', 'multi_select'] as const;

export const PROFILE_FIELD_VISIBILITIES = ['everyone', 'admins'] as const;
export type ProfileFieldVisibility = (typeof PROFILE_FIELD_VISIBILITIES)[number];
export const profileFieldVisibilitySchema = z.enum(PROFILE_FIELD_VISIBILITIES);

export const PROFILE_FIELD_EDITORS = ['self', 'admins'] as const;
export type ProfileFieldEditor = (typeof PROFILE_FIELD_EDITORS)[number];
export const profileFieldEditorSchema = z.enum(PROFILE_FIELD_EDITORS);

/** At most this many non-archived profile fields per workspace. */
export const MAX_ACTIVE_PROFILE_FIELDS = 30;

// ---------------------------------------------------------------- files (D-010)

export const FILE_PURPOSES = [
  'attachment',
  'avatar',
  'workspace_icon',
  'custom_emoji',
  'link_preview_image',
  'slack_import_archive',
  'workspace_export',
  'call_recording',
  'task_attachment',
  'training_content',
  'evidence',
  'transcript',
  'ptstdm_import',
  // Wave 4 (D-800): the same four as `FILE_PURPOSES_WAVE4` (enums/pay.ts), which a test keeps in step.
  // None of them is an upload purpose a client may start (`UPLOAD_PURPOSES`).
  'training_certificate',
  'plan_letter',
  'payout_export',
  'pay_evidence',
] as const;
export type FilePurpose = (typeof FILE_PURPOSES)[number];

export const FILE_STATUSES = [
  'pending_upload',
  'processing',
  'ready',
  'quarantined',
  'failed',
  'deleted',
] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];

export const FILE_SCAN_STATUSES = ['skipped', 'pending', 'clean', 'infected', 'error'] as const;
export type FileScanStatus = (typeof FILE_SCAN_STATUSES)[number];

/** `files.variants[].variant`. Avatars use the three `avatar_*` sizes (512/128/48 px WebP). */
export const FILE_VARIANT_NAMES = [
  'thumb_360',
  'thumb_720',
  'thumb_1440',
  'poster',
  'pdf_preview',
  'avatar_512',
  'avatar_128',
  'avatar_48',
] as const;
export type FileVariantName = (typeof FILE_VARIANT_NAMES)[number];

/** Avatar sizes in pixels, largest first (D-010). */
export const AVATAR_SIZES = [512, 128, 48] as const;
/** Maximum avatar upload size in bytes (5 MB, D-010). */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

// ---------------------------------------------------------------- audit log (D-021)

/** `audit_events.actor_kind`. */
/**
 * `external` (wave 5, D-1038): an outside guest, registrant or booker acting through a public route.
 * Such rows carry the guest id as target, no target label, no IP and no user agent.
 */
export const AUDIT_ACTOR_KINDS = ['user', 'bot', 'system', 'import', 'external'] as const;
export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number];

/**
 * Audit actions (`<entity>.<past_tense>`). Phase 0 list from D-021 and PLAN §4 (audit log); later
 * phases append their own. `audit_events.action` is additionally checked by a pattern CHECK.
 */
export const AUDIT_ACTIONS = [
  'invite.created',
  'invite.resent',
  'invite.revoked',
  'invite.accepted',
  'member.role_changed',
  'member.department_changed',
  'member.manager_changed',
  'member.title_changed',
  // The payroll employee code and the date of joining (D-930).
  'member.employment_changed',
  'member.deactivated',
  'member.reactivated',
  'member.avatar_changed_by_admin',
  'department.created',
  'department.updated',
  'department.archived',
  'department.unarchived',
  'department.deleted',
  'workspace.setting_changed',
  'profile_field.created',
  'profile_field.updated',
  'profile_field.deleted',
  'session.revoked',
  'session.revoked_others',
  'session.revoked_by_admin',
  'session.refresh_reuse_detected',
  'auth.password_reset',
  'auth.password_changed',
  // Phase 1 — messaging (PLAN §4 "Bootstrap, conversations and channels", D-046, D-109)
  'conversation.privacy_changed',
  'conversation.member_removed',
  'conversation.member_role_changed',
  'default_channels.changed',
  'message.deleted',
  'message.deleted_content_viewed',
  'file.deleted',
  // Phase 2 — integrations, import, export, emoji, groups, commands, SSO (PLAN §4 Phase 2 rows)
  'bot.created',
  'bot.updated',
  'bot.owner_changed',
  'bot.disabled',
  'bot.enabled',
  'bot.token_created',
  'bot.token_revoked',
  'webhook.incoming_created',
  'webhook.incoming_updated',
  'webhook.incoming_rotated',
  'webhook.incoming_deleted',
  'webhook.outgoing_created',
  'webhook.outgoing_updated',
  'webhook.outgoing_secret_rotated',
  'webhook.outgoing_deleted',
  'webhook.outgoing_disabled',
  'slash_command.updated',
  'import.started',
  'import.committed',
  'import.cancelled',
  'export.requested',
  'export.downloaded',
  'emoji.created',
  'emoji.deleted',
  'user_group.created',
  'user_group.updated',
  'user_group.members_changed',
  'user_group.disabled',
  'user_group.enabled',
  'auth.sso_linked',
  'auth.sso_login',
  // Phase 4 — tasks (PLAN §3.2 `task_events`: admin-only actions are also audited; D-023, D-036)
  'task.draft_deleted',
  'task.reassigned_by_admin',
  'task.cancelled_by_admin',
  'task.reopened_by_admin',
  'task.verified_by_admin',
  'task.due_date_changed_by_admin',
  'task.decision_recorded_on_behalf',
  'delegation_guard_exception.created',
  'delegation_guard_exception.revoked',
  'department.task_settings_changed',
  'member.offboarded',
  'project.archived',
  // Phase 5 — PTSTDM (PLAN §4 "PTSTDM core and daily tracking", "Integration ingestion points")
  'department.head_changed',
  'department.ptstdm_settings_changed',
  'ptstdm.position_changed',
  'ptstdm.assignment_changed',
  'ptstdm.kpi_changed',
  'ptstdm.scorecard_activated',
  'ptstdm.scorecard_withdrawn',
  'ptstdm.target_changed',
  'ptstdm.kpi_entry_verified',
  'ptstdm.override_approved',
  'ptstdm.import_committed',
  'ptstdm.late_leave_exemption',
  'integration_source.created',
  'integration_source.updated',
  'integration_source.rotated',
  'integration_source.mapping_changed',
  'integration_source.user_link_changed',
  'integration_source.backfill_uploaded',
  'leave.marked',
  'leave.cancelled',
  'holiday.created',
  'holiday.updated',
  'holiday.deleted',
  // Phase 6 — calls (D-647): every call or huddle start, and an Admin ending someone's call.
  // Recording actions (`call.recording_*`, PLAN §4) join when recording is built.
  'call.started',
  'call.ended_by_admin',
  // D-1158: a moderator taking somebody out of a live call.
  'call.participant_removed',
  // Wave 4 — skills (D-817): the library, the levels a seat requires, ratings and proposals.
  'skill.category_created',
  'skill.category_updated',
  'skill.created',
  'skill.updated',
  'skill.archived',
  'skill.restored',
  'skill.requirements_changed',
  'skill.assessment_recorded',
  'skill.proposal_recorded',
  'skill.proposal_confirmed',
  'skill.proposal_rejected',
  'skill.proposal_superseded',
  // Wave 4 — training: a manager waives someone's assignment (who did it; the row keeps the reason).
  'training_assignment.waived',
  // Wave 4 — weekly reviews: an admin's regenerate request and its completion.
  'weekly_review.regenerated',
  // Wave 4 — appraisal (D-861): forms, cycles, calibration, release, reopen and lock.
  'appraisal.form_created',
  'appraisal.form_published',
  'appraisal.form_archived',
  'appraisal.cycle_created',
  'appraisal.cycle_launched',
  'appraisal.calibration_started',
  'appraisal.calibrated',
  'appraisal.released',
  'appraisal.section_reopened',
  'appraisal.cycle_locked',
  // Wave 4 — pay: the member grants (`hr_compensation`, `payroll_export`, `pay_approver`,
  // `cockpit`). Everything else about pay is in the hash-chained `pay_audit_events`.
  'member_grant.granted',
  'member_grant.revoked',
  // The daily pay audit chain check anchors the chain head outside the chain (D-930).
  'pay.audit_chain_anchored',
  // Phase 9 — the optional antivirus scan (SR-7).
  'file.quarantined',
  'file.scan_failed',
  // Wave 5 — calendar, guests, webinars, booking pages, feeds (D-1038). Rows that target a guest,
  // registrant or booker carry ids only: no target label, no name, email or company in metadata.
  'meeting.guest_added',
  'meeting.guest_removed',
  'meeting.guest_link_viewed',
  'meeting.guest_link_reset',
  'meeting.lobby_admitted',
  'meeting.lobby_denied',
  'meeting.participant_removed',
  'meeting.participant_muted',
  'meeting.chair_reassigned',
  'meeting.invitations_released',
  'webinar.created',
  'webinar.published',
  'webinar.registration_decided',
  'webinar.report_exported',
  'booking_page.created',
  'booking_page.status_changed',
  'booking.cancelled_by_host',
  'calendar_feed.created',
  'calendar_feed.rotated',
  'calendar_feed.revoked',
  'calendar_feed.link_viewed',
  'guest.anonymized',
  // Wave 5 — actions of outside people (`actor_kind = 'external'`).
  'meeting.guest_responded',
  'webinar.registered',
  'booking.created',
  'booking.changed_by_guest',
  'meeting.guest_joined',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const auditActionSchema = z.enum(AUDIT_ACTIONS);

/** `audit_events.target_type` values used in Phase 0 (`GET /admin/audit-events?targetType=`). */
export const AUDIT_TARGET_TYPES = [
  'member',
  'invite',
  'department',
  'workspace',
  'session',
  'profile_field',
  'user',
  'setting',
  'conversation',
  'message',
  'file',
  // Phase 2
  'bot',
  'bot_token',
  'incoming_webhook',
  'outgoing_webhook',
  'slash_command',
  'slack_import',
  'workspace_export',
  'custom_emoji',
  'user_group',
  'auth_identity',
  // Phase 4
  'task',
  'project',
  'delegation_guard_exception',
  // Phase 5
  'position',
  'position_assignment',
  'kpi',
  'kpi_target',
  'kpi_entry',
  'scorecard',
  'integration_source',
  'leave',
  'holiday',
  'ptstdm_import',
  // Phase 6
  'call',
  // Wave 4 — skills and appraisal
  'skill_category',
  'skill',
  'skill_assessment',
  'skill_level_proposal',
  'training_assignment',
  'appraisal_form',
  'appraisal_cycle',
  'appraisal',
  // Wave 5 (D-1038)
  'meeting_series',
  'meeting_guest',
  'webinar',
  'booking_page',
  'booking',
  'calendar_feed',
] as const;
export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number];
export const auditTargetTypeSchema = z.enum(AUDIT_TARGET_TYPES);

// ---------------------------------------------------------------- messaging (PLAN §3.1 "Messaging")

/**
 * `conversations.kind`. `task` is the hidden per-task comment stream (D-054, Phase 4): it is never
 * listed by channel, sidebar or switcher queries and never appears in `ConversationDto.kind`.
 */
export const CONVERSATION_KINDS = [
  'public_channel',
  'private_channel',
  'dm',
  'group_dm',
  'self_dm',
  'task',
] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];
export const conversationKindSchema = z.enum(CONVERSATION_KINDS);

/** The kinds clients see (`ConversationDto.kind`): everything except `task`. */
export const LISTED_CONVERSATION_KINDS = [
  'public_channel',
  'private_channel',
  'dm',
  'group_dm',
  'self_dm',
] as const;
export type ListedConversationKind = (typeof LISTED_CONVERSATION_KINDS)[number];
export const listedConversationKindSchema = z.enum(LISTED_CONVERSATION_KINDS);

export const CHANNEL_KINDS = ['public_channel', 'private_channel'] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];
export const channelKindSchema = z.enum(CHANNEL_KINDS);

/** Direct conversations: fixed member set, identified by `dm_key` (sorted member ids). */
export const DIRECT_KINDS = ['dm', 'group_dm', 'self_dm'] as const;
export type DirectKind = (typeof DIRECT_KINDS)[number];

export function isChannelKind(kind: string): kind is ChannelKind {
  return (CHANNEL_KINDS as readonly string[]).includes(kind);
}

export function isDirectKind(kind: string): kind is DirectKind {
  return (DIRECT_KINDS as readonly string[]).includes(kind);
}

/** `conversations.posting_policy` (announcement-style channels). */
export const POSTING_POLICIES = ['everyone', 'admins_only'] as const;
export type PostingPolicy = (typeof POSTING_POLICIES)[number];
export const postingPolicySchema = z.enum(POSTING_POLICIES);

/** `conversation_members.role`. Channel managers can rename, set the topic and remove members. */
export const CONVERSATION_MEMBER_ROLES = ['member', 'channel_manager'] as const;
export type ConversationMemberRole = (typeof CONVERSATION_MEMBER_ROLES)[number];
export const conversationMemberRoleSchema = z.enum(CONVERSATION_MEMBER_ROLES);

/** Per-conversation notification override (`conversation_members.notify_level`, SPEC §4.6). */
export const NOTIFY_LEVELS = ['default', 'all', 'mentions', 'nothing'] as const;
export type NotifyLevel = (typeof NOTIFY_LEVELS)[number];
export const notifyLevelSchema = z.enum(NOTIFY_LEVELS);

/** The member's desktop default (`notification_preferences.desktop_level`). */
export const DESKTOP_NOTIFY_LEVELS = ['all', 'mentions', 'nothing'] as const;
export type DesktopNotifyLevel = (typeof DESKTOP_NOTIFY_LEVELS)[number];
export const desktopNotifyLevelSchema = z.enum(DESKTOP_NOTIFY_LEVELS);

/** The member's mobile default (`notification_preferences.mobile_level`, Phase 3). */
export const MOBILE_NOTIFY_LEVELS = ['same', 'all', 'mentions', 'nothing'] as const;
export type MobileNotifyLevel = (typeof MOBILE_NOTIFY_LEVELS)[number];
export const mobileNotifyLevelSchema = z.enum(MOBILE_NOTIFY_LEVELS);

/** `messages.kind`. */
export const MESSAGE_KINDS = ['standard', 'system', 'card'] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];
export const messageKindSchema = z.enum(MESSAGE_KINDS);

/** `messages.subtype` for `kind = 'system'`. */
export const SYSTEM_MESSAGE_SUBTYPES = [
  'member_joined',
  'member_left',
  'member_added',
  'member_removed',
  'channel_created',
  'channel_renamed',
  'channel_archived',
  'channel_unarchived',
  'topic_changed',
  'description_changed',
  'privacy_changed',
  /**
   * D-1144: who may post here changed. It belongs in the channel, visibly and permanently, for
   * the same reason `privacy_changed` does — somebody who could write here yesterday and cannot
   * today is owed the reason in the place they will look for it.
   */
  'posting_policy_changed',
  'message_pinned',
  /** Phase 2: an outgoing webhook now receives this private channel's events (PLAN §4). */
  'integration_connected',
  'call',
  'huddle_summary',
  'recording_notice',
  'task_event',
  'reminder',
  'import_notice',
  /**
   * Wave 5 (D-1015): a calendar meeting hosted in a channel announces itself once ("scheduled Client
   * sync · Tue 14 Oct 2026, 3:00 PM"); its rich text links to the meeting page.
   */
  'meeting_scheduled',
] as const;
export type SystemMessageSubtype = (typeof SYSTEM_MESSAGE_SUBTYPES)[number];

/** `messages.subtype` for `kind = 'card'`. */
export const CARD_MESSAGE_SUBTYPES = [
  'agenda_card',
  'report_card',
  'meeting_record',
  'weekly_review',
  'digest',
] as const;
export type CardMessageSubtype = (typeof CARD_MESSAGE_SUBTYPES)[number];

export const MESSAGE_SUBTYPES = [...SYSTEM_MESSAGE_SUBTYPES, ...CARD_MESSAGE_SUBTYPES] as const;
export type MessageSubtype = (typeof MESSAGE_SUBTYPES)[number];

/** `messages.delete_reason`. */
export const MESSAGE_DELETE_REASONS = [
  'author',
  'admin',
  'bot',
  'retention',
  'conversation_purged',
] as const;
export type MessageDeleteReason = (typeof MESSAGE_DELETE_REASONS)[number];

/** `message_edits.action` (D-046). */
export const MESSAGE_EDIT_ACTIONS = ['edit', 'delete'] as const;
export type MessageEditAction = (typeof MESSAGE_EDIT_ACTIONS)[number];

/** `message_mentions.kind`. `@channel` and `@here` are one row each (D-052). */
export const MENTION_KINDS = ['user', 'group', 'channel', 'here', 'keyword'] as const;
export type MentionKind = (typeof MENTION_KINDS)[number];

/** `thread_subscriptions.reason`: why someone follows a thread. */
export const THREAD_SUBSCRIPTION_REASONS = ['author', 'replied', 'mentioned', 'manual'] as const;
export type ThreadSubscriptionReason = (typeof THREAD_SUBSCRIPTION_REASONS)[number];

/**
 * `activity_items.kind` (PLAN §3.1). Phase 1: mentions, threads, reactions. Phase 2 adds
 * `reminder` (a fired reminder) and `scheduled_message_failed` (a scheduled send that could not be
 * delivered, D-177). Phase 4 adds the task kinds (items carry `activity_items.task_id`). PTSTDM
 * and call kinds join the list by migration in the phase that adds them.
 */
export const ACTIVITY_KINDS = [
  'mention',
  'group_mention',
  'broadcast_mention',
  'keyword',
  'thread_reply',
  'reaction',
  // Phase 2
  'reminder',
  'scheduled_message_failed',
  // Phase 4 — tasks and decisions
  'task_assigned',
  'task_accepted',
  'task_pushed_back',
  'task_declined',
  'task_due_today',
  'task_overdue',
  'task_done',
  'task_verified',
  'task_reopened',
  'task_comment',
  'decision_requested',
  'decision_made',
  // Phase 5 — PTSTDM (PLAN §3.3 sec. 16).
  'checklist_verification_needed',
  'kpi_verification_needed',
  'agenda_reminder',
  'report_reminder',
] as const;
/** The Phase 4 task kinds of {@link ACTIVITY_KINDS}. */
export const TASK_ACTIVITY_KINDS = [
  'task_assigned',
  'task_accepted',
  'task_pushed_back',
  'task_declined',
  'task_due_today',
  'task_overdue',
  'task_done',
  'task_verified',
  'task_reopened',
  'task_comment',
  'decision_requested',
  'decision_made',
] as const satisfies readonly (typeof ACTIVITY_KINDS)[number][];
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];
export const activityKindSchema = z.enum(ACTIVITY_KINDS);

/** `file_uploads.mode`: one presigned PUT up to 100 MiB, multipart above (D-010). */
export const UPLOAD_MODES = ['single', 'multipart'] as const;
export type UploadMode = (typeof UPLOAD_MODES)[number];

/** How clients preview a file (`FileDto.previewKind`), derived from the sniffed MIME type. */
export const FILE_PREVIEW_KINDS = ['image', 'video', 'audio', 'pdf', 'text', 'none'] as const;
export type FilePreviewKind = (typeof FILE_PREVIEW_KINDS)[number];

/**
 * Text snippets (D-1261), the Slack feature: a piece of text posted as a file rather than as a
 * message — longer than a message may be, kept whole, syntax-highlighted, previewed in the
 * conversation and expandable. A snippet IS a file (`files.snippet_language` is what makes it one),
 * so it attaches, downloads, searches, scans and expires exactly like any other upload.
 *
 * The languages are the ones Slack offers, named as Slack names them; `plaintext` is the default
 * and the one a long paste becomes. The value is stored, so only ever add to this list.
 */
export const SNIPPET_LANGUAGES = [
  'plaintext',
  'bash',
  'c',
  'cpp',
  'csharp',
  'css',
  'diff',
  'go',
  'html',
  'java',
  'javascript',
  'json',
  'kotlin',
  'markdown',
  'objectivec',
  'perl',
  'php',
  'python',
  'ruby',
  'rust',
  'scala',
  'sql',
  'swift',
  'typescript',
  'xml',
  'yaml',
] as const;
export type SnippetLanguage = (typeof SNIPPET_LANGUAGES)[number];

/** What each language is called on screen (the composer menu and the snippet card). */
export const SNIPPET_LANGUAGE_LABELS: Readonly<Record<SnippetLanguage, string>> = Object.freeze({
  plaintext: 'Plain text',
  bash: 'Shell',
  c: 'C',
  cpp: 'C++',
  csharp: 'C#',
  css: 'CSS',
  diff: 'Diff',
  go: 'Go',
  html: 'HTML',
  java: 'Java',
  javascript: 'JavaScript',
  json: 'JSON',
  kotlin: 'Kotlin',
  markdown: 'Markdown',
  objectivec: 'Objective-C',
  perl: 'Perl',
  php: 'PHP',
  python: 'Python',
  ruby: 'Ruby',
  rust: 'Rust',
  scala: 'Scala',
  sql: 'SQL',
  swift: 'Swift',
  typescript: 'TypeScript',
  xml: 'XML',
  yaml: 'YAML',
});

/** Snippet limits (D-1261); the server enforces every one of them. */
export const SNIPPET_LIMITS = Object.freeze({
  /** Characters in one snippet — Slack stops at a megabyte of text, and so do we. */
  maxContentChars: 1_000_000,
  /** The title, which is also the file name people download. */
  maxTitleChars: 200,
  /** Lines of the preview card shown in the conversation before "Show more". */
  previewLines: 12,
  /** And how much of it, whatever the lines hold. */
  previewChars: 1_200,
  /** What `GET /files/:id/snippet` returns in one go; more than this is `truncated`. */
  maxInlineChars: 200_000,
});

/** `FileDto.variants`: the message-attachment thumbnails and previews (no avatar sizes). */
export const ATTACHMENT_VARIANT_NAMES = [
  'thumb_360',
  'thumb_720',
  'thumb_1440',
  'poster',
  'pdf_preview',
] as const;
export type AttachmentVariantName = (typeof ATTACHMENT_VARIANT_NAMES)[number];

/** What people see of each other's live presence (D-014). `offline` is shown as `away`. */
export const PRESENCE_STATES = ['active', 'away'] as const;
export type PresenceState = (typeof PRESENCE_STATES)[number];
export const presenceStateSchema = z.enum(PRESENCE_STATES);

/** `PUT /me/presence` modes: automatic, or manual away (`workspace_members.presence_override`). */
export const PRESENCE_MODES = PRESENCE_OVERRIDES;
export const presenceModeSchema = z.enum(PRESENCE_OVERRIDES);

/** Realtime client platforms (`auth.d.client.platform`, PLAN §5). Presence is tracked per device. */
export const CLIENT_PLATFORMS = ['web', 'desktop', 'ios', 'android'] as const;
export type ClientPlatform = (typeof CLIENT_PLATFORMS)[number];
export const clientPlatformSchema = z.enum(CLIENT_PLATFORMS);

/** UI density (`/me/preferences`). */
export const DENSITIES = ['comfortable', 'compact'] as const;
export type Density = (typeof DENSITIES)[number];
export const densitySchema = z.enum(DENSITIES);

/** Sidebar sort (`/me/preferences.sidebarSort`). */
export const SIDEBAR_SORTS = ['alpha', 'recent'] as const;
export type SidebarSort = (typeof SIDEBAR_SORTS)[number];

/**
 * Messaging limits (PLAN §3.1 "Message body storage", §4 "Messages"). The server enforces every
 * one of them; clients use them to warn early.
 */
export const MESSAGING_LIMITS = Object.freeze({
  /** `messages.plain_text` (CHECK `char_length(plain_text) <= 40000`). */
  maxPlainTextChars: 40_000,
  /**
   * What one message a person (or a bot) sends may carry, in characters of plain text: 4,000, the
   * same number Slack has used for years (D-1261). It is far below `maxPlainTextChars`, which
   * stays the storage ceiling for bodies the server re-derives — an import, a forward, a task note
   * written before this limit existed. Going over is not a dead end: the composer offers to turn
   * the text into a snippet, which is what Slack does with a long paste.
   */
  maxMessageChars: 4_000,
  /**
   * What a composer may hold before it offers to make a snippet of it instead (D-1261). Slack
   * starts offering well before the hard limit, so a long paste becomes a file rather than a
   * refusal; below this a person is simply told how many characters are left.
   */
  snippetOfferChars: 3_000,
  /** `content_json` as UTF-8 JSON (the server also CHECKs `pg_column_size`). */
  maxContentBytes: 256 * 1024,
  /**
   * `content_enc`, the sealed body (D-1132). One envelope holds the document AND its plain text,
   * so the plaintext inside it reaches `maxContentBytes` plus up to 4 bytes per character of
   * `maxPlainTextChars`; base64 then adds a third, and the JSON framing a little more. The bound
   * is deliberately loose — it exists to stop a runaway row, not to re-check a length the database
   * can no longer see. The real limits are enforced on the cleartext before it is sealed.
   */
  maxSealedBytes: 768 * 1024,
  /** Mentions per message (users, groups, @channel, @here). */
  maxMentions: 50,
  /** Nesting depth of the ProseMirror document (lists inside quotes inside lists …). */
  maxContentDepth: 16,
  /** Nodes in one document. */
  maxContentNodes: 10_000,
  /** `message_files.position` 0–19. */
  maxFilesPerMessage: 20,
  /** Distinct emoji per message. */
  maxReactionsPerMessage: 50,
  /** `ReactionDto.userIds` (the full list is `GET /messages/:id/reactions`). */
  maxReactionUserIds: 50,
  /** Group DMs: 3–9 people including the creator (SPEC §4.2). */
  groupDmMaxMembers: 9,
  /** `messages.reply_user_ids` keeps the 10 newest distinct repliers. */
  maxStoredRepliers: 10,
  /** `MessageDto.thread.recentReplierIds`. */
  maxRecentRepliers: 5,
  /** Unread and mention counters are capped (the UI shows 99+, D-043). */
  counterCap: 100,
  /** Channel names (`conversations.name`). */
  maxChannelNameChars: 80,
  maxTopicChars: 250,
  maxDescriptionChars: 1_000,
});

/**
 * Search (D-1262). The recents list the search box offers back, and what one query may be.
 */
export const SEARCH_LIMITS = Object.freeze({
  /** Recent searches kept per person; older ones fall off when a new one is remembered. */
  maxRecents: 20,
  /** What the box shows without being asked (Slack shows a handful and no more). */
  shownRecents: 5,
  /** Recently visited conversations offered when the box is still empty ("Recent places"). */
  recentPlaces: 5,
  /** A query, in characters. The box's `maxLength` and the column's CHECK are the same number. */
  maxQueryChars: 500,
});

/** Standard shortcode or custom emoji name, with an optional skin tone (reactions, emoji nodes). */
export const EMOJI_NAME_PATTERN = /^[a-z0-9_+-]{1,64}(::skin-tone-[2-6])?$/;

/**
 * Channel names as sent: lowercase letters in any script, combining marks, digits, `-` and `_`,
 * 1–80 characters (PLAN §4 `POST /conversations`). The database CHECK rejects whitespace and
 * `#@,.:` and requires lowercase.
 */
export const CHANNEL_NAME_PATTERN = /^[\p{Ll}\p{Lo}\p{M}\p{Nd}_-]{1,80}$/u;

// ================================================================ Phase 2 (PLAN §3.1, §4 Phase 2 rows)

// ---------------------------------------------------------------- sidebar sections (SPEC §4.2, D-047)

/** `sidebar_sections.kind`: custom sections, plus rows that store the system sections' state. */
export const SIDEBAR_SECTION_KINDS = [
  'custom',
  'starred',
  'channels',
  'direct_messages',
  'apps',
] as const;
export type SidebarSectionKind = (typeof SIDEBAR_SECTION_KINDS)[number];
export const sidebarSectionKindSchema = z.enum(SIDEBAR_SECTION_KINDS);

/** `sidebar_sections.sort_mode`. */
export const SIDEBAR_SECTION_SORTS = ['manual', 'alpha', 'recent'] as const;
export type SidebarSectionSort = (typeof SIDEBAR_SECTION_SORTS)[number];
export const sidebarSectionSortSchema = z.enum(SIDEBAR_SECTION_SORTS);

/** `sidebar_sections.show`: everything, or only conversations with unread messages. */
export const SIDEBAR_SECTION_SHOW = ['all', 'unreads'] as const;
export type SidebarSectionShow = (typeof SIDEBAR_SECTION_SHOW)[number];
export const sidebarSectionShowSchema = z.enum(SIDEBAR_SECTION_SHOW);

// ---------------------------------------------------------------- channel extras (SPEC §4.2, §4.3)

/** `conversation_bookmarks.kind`. */
export const BOOKMARK_KINDS = ['link', 'file', 'message'] as const;
export type BookmarkKind = (typeof BOOKMARK_KINDS)[number];
export const bookmarkKindSchema = z.enum(BOOKMARK_KINDS);

/** `link_previews.status`. `blocked`: the SSRF guard or the domain blocklist refused it (D-104). */
export const LINK_PREVIEW_STATUSES = ['pending', 'ready', 'failed', 'blocked'] as const;
export type LinkPreviewStatus = (typeof LINK_PREVIEW_STATUSES)[number];

/** Who may manage user groups / upload custom emoji (`messaging.userGroupsManagedBy`, `messaging.emojiUploadBy`). */
export const MANAGED_BY_OPTIONS = ['admins', 'managers', 'everyone'] as const;
export type ManagedBy = (typeof MANAGED_BY_OPTIONS)[number];
export const managedBySchema = z.enum(MANAGED_BY_OPTIONS);

/** Mentionable user group handles (`user_groups.handle`), never `here`, `channel` or `everyone`. */
export const USER_GROUP_HANDLE_PATTERN = /^[a-z0-9][a-z0-9._-]{0,79}$/;
export const RESERVED_GROUP_HANDLES = ['here', 'channel', 'everyone'] as const;

/** Custom emoji names (`custom_emoji.name`); must not equal a standard shortcode (service check). */
export const CUSTOM_EMOJI_NAME_PATTERN = /^[a-z0-9_+-]{1,64}$/;

// ---------------------------------------------------------------- personal productivity (SPEC §4.3, §4.5)

/** `saved_items.status` (the Later list tabs). */
export const SAVED_ITEM_STATUSES = ['in_progress', 'completed', 'archived'] as const;
export type SavedItemStatus = (typeof SAVED_ITEM_STATUSES)[number];
export const savedItemStatusSchema = z.enum(SAVED_ITEM_STATUSES);

/** `reminders.status`. A recurring reminder stays `pending`; a one-off moves to `fired`. */
export const REMINDER_STATUSES = ['pending', 'fired', 'completed', 'cancelled'] as const;
export type ReminderStatus = (typeof REMINDER_STATUSES)[number];
export const reminderStatusSchema = z.enum(REMINDER_STATUSES);

/** "Remind me about this" presets (SPEC §4.3): 20 min, 1 hr, 3 hr, tomorrow 09:00 (creator's zone). */
export const REMINDER_PRESETS = ['20m', '1h', '3h', 'tomorrow_9am'] as const;
export type ReminderPreset = (typeof REMINDER_PRESETS)[number];
export const reminderPresetSchema = z.enum(REMINDER_PRESETS);

/** `scheduled_messages.status`. */
export const SCHEDULED_MESSAGE_STATUSES = [
  'scheduled',
  'sending',
  'sent',
  'failed',
  'cancelled',
] as const;
export type ScheduledMessageStatus = (typeof SCHEDULED_MESSAGE_STATUSES)[number];
export const scheduledMessageStatusSchema = z.enum(SCHEDULED_MESSAGE_STATUSES);

/** `PUT /me/status` `clearAfter` (computed in the member's timezone). */
export const STATUS_CLEAR_AFTER = ['never', '30m', '1h', '4h', 'today', 'this_week'] as const;
export type StatusClearAfter = (typeof STATUS_CLEAR_AFTER)[number];
export const statusClearAfterSchema = z.enum(STATUS_CLEAR_AFTER);

/** `/mute` durations (toggles when the conversation is already muted). */
export const MUTE_DURATIONS = ['30m', '1h', '8h', 'forever'] as const;
export type MuteDuration = (typeof MUTE_DURATIONS)[number];

/** Phase 2 personal limits (PLAN §3.1). The server enforces every one of them. */
export const PRODUCTIVITY_LIMITS = Object.freeze({
  /** `sidebar_sections` of kind `custom` per member. */
  maxCustomSections: 50,
  sectionNameChars: 40,
  /** `conversation_bookmarks` per conversation. */
  maxBookmarksPerConversation: 50,
  bookmarkTitleChars: 100,
  /** `message_pins` per conversation. */
  maxPinsPerConversation: 100,
  /** Scheduled messages in `scheduled` state per member. */
  maxScheduledPerUser: 100,
  /** `send_at` window: at least now + 1 minute, at most now + 120 days. */
  scheduleMinLeadMs: 60_000,
  scheduleMaxLeadMs: 120 * 86_400_000,
  reminderTextChars: 500,
  statusTextChars: 100,
  /** `notification_keywords` per member, each 2–50 characters. */
  maxKeywords: 50,
  keywordMinChars: 2,
  keywordMaxChars: 50,
  /** Link previews per message (`message_link_previews.position`). */
  maxUnfurlsPerMessage: 5,
  /** Messages forwarded to at most this many conversations at once. */
  maxForwardTargets: 10,
});

// ---------------------------------------------------------------- slash commands (SPEC §4.3)

/** `slash_commands.kind`. */
export const SLASH_COMMAND_KINDS = ['builtin', 'bot'] as const;
export type SlashCommandKind = (typeof SLASH_COMMAND_KINDS)[number];

/**
 * Built-in command handler keys (`slash_commands.handler_key`) with the phase that ships each
 * handler. A built-in whose phase is not live is hidden and answers `404 command_not_found`, never
 * a stub (PLAN §3.1 `slash_commands.available_from_phase`).
 */
export const BUILTIN_SLASH_COMMANDS = {
  remind: { phase: 2 },
  status: { phase: 2 },
  mute: { phase: 2 },
  invite: { phase: 2 },
  task: { phase: 4 },
  agenda: { phase: 5 },
  report: { phase: 5 },
  checklist: { phase: 5 },
  call: { phase: 6 },
  scorecard: { phase: 7 },
} as const;
export type BuiltinSlashCommand = keyof typeof BUILTIN_SLASH_COMMANDS;
export const BUILTIN_SLASH_COMMAND_KEYS = Object.keys(
  BUILTIN_SLASH_COMMANDS,
) as BuiltinSlashCommand[];

/** `/[a-z][a-z0-9_-]{1,31}`, the `slash_commands.command` CHECK. */
export const SLASH_COMMAND_PATTERN = /^\/[a-z][a-z0-9_-]{1,31}$/;

/** What a command answers (`POST /conversations/:id/commands`). */
export const SLASH_RESPONSE_TYPES = ['ephemeral', 'in_channel', 'open_modal', 'none'] as const;
export type SlashResponseType = (typeof SLASH_RESPONSE_TYPES)[number];
export const slashResponseTypeSchema = z.enum(SLASH_RESPONSE_TYPES);

// ---------------------------------------------------------------- bots and webhooks (SPEC §4.7, D-072)

/**
 * Bot token scopes (PLAN §4 "Bots and bot tokens"). `tasks:write` reaches `POST /tasks` and
 * `tasks:read` `GET /tasks/:taskId` (D-368); `kpi:ingest` is deliberately not a scope.
 */
export const BOT_SCOPES = [
  'channels:read',
  'channels:history',
  'channels:join',
  'messages:write',
  'messages:write.public',
  'reactions:write',
  'files:read',
  'files:write',
  'users:read',
  'users:read.email',
  'tasks:read',
  'tasks:write',
  'reminders:write',
  'commands:register',
] as const;
export type BotScope = (typeof BOT_SCOPES)[number];
export const botScopeSchema = z.enum(BOT_SCOPES);

/** `bots.status`. Disabling also deactivates the bot's member row. */
export const BOT_STATUSES = ['active', 'disabled'] as const;
export type BotStatus = (typeof BOT_STATUSES)[number];

/** `bots.handle`. */
export const BOT_HANDLE_PATTERN = /^[a-z0-9-]{1,40}$/;

/** Credential prefixes (D-072): bot token, integration-source token, signing secret. */
export const CREDENTIAL_PREFIXES = Object.freeze({
  botToken: 'hubb_',
  integrationToken: 'hubi_',
  signingSecret: 'hubws_',
});

/** `outgoing_webhooks.status`. */
export const OUTGOING_WEBHOOK_STATUSES = ['active', 'disabled'] as const;
export type OutgoingWebhookStatus = (typeof OUTGOING_WEBHOOK_STATUSES)[number];
export const outgoingWebhookStatusSchema = z.enum(OUTGOING_WEBHOOK_STATUSES);

/** `webhook_deliveries.status`. */
export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'succeeded', 'failed', 'dead'] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];
export const webhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES);

/**
 * Outgoing webhook event types with the phase that starts emitting them (PLAN §5 "Outgoing
 * integration webhooks"). Endpoints may subscribe only to events whose phase is live.
 */
export const WEBHOOK_EVENT_TYPES = {
  ping: { phase: 2 },
  'message.created': { phase: 2 },
  'message.updated': { phase: 2 },
  'message.deleted': { phase: 2 },
  'reaction.added': { phase: 2 },
  'reaction.removed': { phase: 2 },
  'conversation.created': { phase: 2 },
  'conversation.archived': { phase: 2 },
  'conversation.member.joined': { phase: 2 },
  'conversation.member.left': { phase: 2 },
  'member.joined': { phase: 2 },
  'member.deactivated': { phase: 2 },
  'task.created': { phase: 4 },
  'task.updated': { phase: 4 },
  'decision.decided': { phase: 4 },
  'task.comment_created': { phase: 4 },
  'checklist.instance.completed': { phase: 5 },
  'checklist.instance.missed': { phase: 5 },
  'agenda.submitted': { phase: 5 },
  'report.submitted': { phase: 5 },
  'report.missed': { phase: 5 },
  'kpi.entry.verified': { phase: 5 },
  'call.ended': { phase: 6 },
  'meeting.concluded': { phase: 7 },
} as const;
export type WebhookEventType = keyof typeof WEBHOOK_EVENT_TYPES;
export const WEBHOOK_EVENT_TYPE_KEYS = Object.keys(WEBHOOK_EVENT_TYPES) as WebhookEventType[];
export const webhookEventTypeSchema = z.enum(
  WEBHOOK_EVENT_TYPE_KEYS as [WebhookEventType, ...WebhookEventType[]],
);

/**
 * Retry schedule of outgoing webhooks after the first attempt (D-072): 30 s, 2 min, 10 min,
 * 30 min, 1 h, 3 h, 6 h, 12 h, 24 h, then `dead`.
 */
export const WEBHOOK_RETRY_DELAYS_MS = Object.freeze([
  30_000, 120_000, 600_000, 1_800_000, 3_600_000, 10_800_000, 21_600_000, 43_200_000, 86_400_000,
]);

/** Who manages a channel's incoming webhooks (`integrations.webhooksManagedBy`). */
export const WEBHOOKS_MANAGED_BY = ['admins', 'channel_members'] as const;
export type WebhooksManagedBy = (typeof WEBHOOKS_MANAGED_BY)[number];

/** Incoming-webhook attachment colours (besides `#RRGGBB`). */
export const WEBHOOK_ATTACHMENT_COLORS = ['good', 'warning', 'danger'] as const;

// ---------------------------------------------------------------- Slack import and export (SPEC §4.8, §4.9)

/** `slack_imports.status`. */
export const SLACK_IMPORT_STATUSES = [
  'uploaded',
  'analyzing',
  'dry_run_ready',
  'committing',
  'completed',
  'failed',
  'cancelled',
] as const;
export type SlackImportStatus = (typeof SLACK_IMPORT_STATUSES)[number];
export const slackImportStatusSchema = z.enum(SLACK_IMPORT_STATUSES);

/** `slack_import_id_maps.slack_kind`. */
export const SLACK_ID_KINDS = ['user', 'channel', 'message', 'file', 'emoji'] as const;
export type SlackIdKind = (typeof SLACK_ID_KINDS)[number];

/** `options.createMissingUsersAs`: placeholder members, invites (allowed domains only) or nothing. */
export const SLACK_MISSING_USER_ACTIONS = ['deactivated', 'invite', 'skip'] as const;
export type SlackMissingUserAction = (typeof SLACK_MISSING_USER_ACTIONS)[number];

/** `options.channelConflict`: imports never merge into an existing conversation. */
export const SLACK_CHANNEL_CONFLICT_ACTIONS = ['rename', 'skip'] as const;
export type SlackChannelConflictAction = (typeof SLACK_CHANNEL_CONFLICT_ACTIONS)[number];

/** Why an archive was refused (`422 archive_unsafe`, `details.reason`). */
export const ARCHIVE_UNSAFE_REASONS = [
  'path_traversal',
  'symlink',
  'too_many_entries',
  'too_large',
  'compression_ratio',
] as const;
export type ArchiveUnsafeReason = (typeof ARCHIVE_UNSAFE_REASONS)[number];

/** Archive safety limits (PLAN §3.1 `slack_imports`, architecture §5.7). */
export const SLACK_ARCHIVE_LIMITS = Object.freeze({
  maxEntries: 100_000,
  maxEntryBytes: 2 * 1024 * 1024 * 1024,
  /** Total uncompressed bytes ≤ this × the archive size. */
  maxTotalRatio: 4,
  /** Per-entry compression ratio. */
  maxCompressionRatio: 100,
  maxJsonFileBytes: 256 * 1024 * 1024,
});

/** Slack's file hosts: the only download origins an import accepts (https only). */
export const SLACK_FILE_HOST_PATTERNS = Object.freeze([
  /^files\.slack\.com$/,
  /\.slack-edge\.com$/,
]);

/** `workspace_exports.status`. */
export const WORKSPACE_EXPORT_STATUSES = [
  'queued',
  'running',
  'completed',
  'failed',
  'expired',
  'cancelled',
] as const;
export type WorkspaceExportStatus = (typeof WORKSPACE_EXPORT_STATUSES)[number];
export const workspaceExportStatusSchema = z.enum(WORKSPACE_EXPORT_STATUSES);

/** `async_jobs.kind` (PLAN §3.1 "Platform tables"). */
export const ASYNC_JOB_KINDS = [
  'slack_import',
  'workspace_export',
  'ptstdm_import',
  'pay_compute',
  'transcript_extract',
  'kpi_backfill',
  'pdf_render',
  // Wave 4 (D-808): an admin's `POST /admin/weekly-reviews/regenerate` is tracked like the others.
  'weekly_review',
] as const;
export type AsyncJobKind = (typeof ASYNC_JOB_KINDS)[number];
export const asyncJobKindSchema = z.enum(ASYNC_JOB_KINDS);

/** `async_jobs.status`. */
export const ASYNC_JOB_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
] as const;
export type AsyncJobStatus = (typeof ASYNC_JOB_STATUSES)[number];
export const asyncJobStatusSchema = z.enum(ASYNC_JOB_STATUSES);

// ---------------------------------------------------------------- search and navigation (SPEC §4.4)

/** Search result tabs (`GET /search/*`); `tasks` answers from Phase 4. */
export const SEARCH_TABS = ['messages', 'files', 'channels', 'people', 'tasks'] as const;
export type SearchTab = (typeof SEARCH_TABS)[number];

/** `GET /search/messages?sort=`. */
export const SEARCH_SORTS = ['relevance', 'recent'] as const;
export type SearchSort = (typeof SEARCH_SORTS)[number];
export const searchSortSchema = z.enum(SEARCH_SORTS);

/** Quick switcher result kinds (`GET /switcher`). */
export const SWITCHER_ITEM_KINDS = ['conversation', 'person', 'recent'] as const;
export type SwitcherItemKind = (typeof SWITCHER_ITEM_KINDS)[number];

/** Activity feed filters (`GET /activity?filter=`). `tasks` and `ptstdm` fill from Phases 4 and 5. */
export const ACTIVITY_FILTERS = [
  'all',
  /** D-1189: everything that happened in a one-to-one or group DM, however it reached you. */
  'dms',
  'mentions',
  'reactions',
  'threads',
  'tasks',
  'ptstdm',
  'unread',
] as const;
export type ActivityFilter = (typeof ACTIVITY_FILTERS)[number];
export const activityFilterSchema = z.enum(ACTIVITY_FILTERS);
